import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import FooterAlt from "@/components/FooterAlt";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import StructuredData from "@/components/StructuredData";

const FAQ = () => {
  useEffect(() => {
    document.title = "FAQ: AMX Ventures Investment Approach & X-Factor Model";
    const desc = "Common questions about AMX Ventures: What is the X-Factor model? How do we help portfolio companies? Who is Arun Mohan? Investment criteria, ecosystem-led growth strategy explained.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", window.location.href);
  }, []);

  const faqItems = [
    {
      question: "What is AMX Ventures?",
      answer: "AMX Ventures is a venture capital firm that invests in B2B SaaS and enterprise software companies. We provide capital that comes with channels — connecting founders with hyperscaler ecosystems (AWS, Microsoft, Google Cloud, Oracle, Salesforce) to accelerate growth through strategic partnerships."
    },
    {
      question: "What is the X-Factor model?",
      answer: "The X-Factor model is our unique investment approach focused on ecosystem alignment. Instead of just providing capital, we help founders scale through hyperscaler partnerships, driving sustainable growth through marketplace listings, co-sell opportunities, and joint GTM strategies. This creates capital-efficient, scalable businesses with 80%+ lower CAC."
    },
    {
      question: "What stage companies does AMX Ventures invest in?",
      answer: "We invest in both Seed and Acquired (growth) stage companies. For seed investments, we look for founders with strong vision and ecosystem fit. For acquired companies, we partner with proven businesses ready to scale through hyperscaler channels."
    },
    {
      question: "Who is Arun Mohan?",
      answer: "Arun Mohan is the Investor at AMX Ventures. He has 15+ years of experience building and scaling ventures, with two past exits (Adfolks and AppsIntegra). He has achieved 80% CAC reduction through ecosystem-led GTM and has invested in 6 portfolio companies following the X-Factor model."
    },
    {
      question: "What industries does AMX Ventures focus on?",
      answer: "We focus on B2B SaaS, Cloud Infrastructure, Enterprise Software, Cloud Operations, AI/ML platforms, and DevOps tools — particularly companies that can leverage hyperscaler ecosystems for distribution and growth."
    },
    {
      question: "How does AMX Ventures help portfolio companies grow?",
      answer: "We provide three key elements: Capital for growth, Strategic guidance on ecosystem partnerships, and Direct connections to hyperscaler GTM teams (AWS, Microsoft, Google Cloud, Oracle, Salesforce). This enables co-sell opportunities, marketplace listings, and joint marketing initiatives."
    },
    {
      question: "What is ecosystem-led growth?",
      answer: "Ecosystem-led growth means building your GTM strategy around hyperscaler partnerships rather than traditional sales. By aligning with platforms like AWS or Microsoft Azure, companies gain instant credibility, access to enterprise customers, and significantly lower customer acquisition costs."
    },
    {
      question: "How can I pitch to AMX Ventures?",
      answer: "Schedule a meeting with us through our website. We're looking for ambitious founders solving real enterprise problems, with products that can integrate into hyperscaler ecosystems. Strong founding teams with domain expertise and clear vision are essential."
    },
    {
      question: "What is the typical investment size?",
      answer: "Investment sizes vary based on stage and needs. For seed rounds, we typically invest as part of a syndicate. For growth-stage companies, we can lead or co-lead larger rounds. Contact us to discuss your specific funding requirements."
    },
    {
      question: "What makes AMX Ventures different from other VCs?",
      answer: "Unlike traditional VCs that focus solely on capital deployment, we bring deep hyperscaler ecosystem expertise. Our X-Factor model has proven results: 80% CAC reduction, faster sales cycles, and higher win rates through strategic partnerships. We're operators who have built and scaled companies ourselves."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <StructuredData data={faqSchema} />
      <Navigation />

      <main className="container mx-auto px-4 py-16">
        <header className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground">
            Everything you need to know about AMX Ventures, our investment approach, and the X-Factor model.
          </p>
        </header>

        <section className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-2 border-border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <footer className="max-w-3xl mx-auto mt-16 text-center">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <a
            href="/schedule"
            className="inline-block px-6 py-3 bg-primary text-primary-foreground font-bold rounded hover:bg-primary/90 transition-colors"
          >
            Schedule a Meeting
          </a>
        </footer>
      </main>

      <FooterAlt />
    </div>
  );
};

export default FAQ;
